<template>
  <PrintPageLandscape>
    <PresentationPage :page-number="pageNumber" :page-count="pageCount">
      <p class="presentation-page__eyebrow">Darlehen &amp; Cashflow</p>
      <h2 class="presentation-page__title">Tilgungsplan — Darlehensstruktur</h2>
      <p class="presentation-page__text">
        Darlehen {{ seniorLoan }} · {{ durationYears }} Jahre · Zinssatz {{ interestRatePercent }} ·
        Tilgung {{ repaymentRatePercent }} · Festschreibung {{ fixedRateYears }}.
      </p>
      <div class="presentation-page__loan-layout">
        <table class="presentation-page__data-table presentation-page__data-table--loan">
          <thead>
            <tr>
              <th>Jahr</th>
              <th>Erlöse GF (€)</th>
              <th>Zins (€)</th>
              <th>Tilgung (€)</th>
              <th>CF n.St. (€)</th>
              <th>CF p.m.</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in loanRows" :key="row.loanYear">
              <td class="presentation-page__data-table__cell--year">{{ row.year }}</td>
              <td class="presentation-page__data-table__cell--numeric">{{ row.revenue }}</td>
              <td class="presentation-page__data-table__cell--numeric">{{ row.interest }}</td>
              <td class="presentation-page__data-table__cell--numeric">{{ row.repayment }}</td>
              <td class="presentation-page__data-table__cell--numeric">{{ row.cashflowYear }}</td>
              <td class="presentation-page__data-table__cell--numeric">{{ row.cashflowMonth }}</td>
            </tr>
          </tbody>
        </table>
        <aside>
          <div class="presentation-page__meta-panel">
            <div
              class="presentation-page__meta-panel-header presentation-page__meta-panel-header--brand-secondary"
            >
              Darlehenseckdaten
            </div>
            <div v-for="row in loanMeta" :key="row.label" class="presentation-page__meta-panel-row">
              <span class="presentation-page__meta-panel-label">{{ row.label }}</span>
              <span class="presentation-page__meta-panel-value">{{ row.value }}</span>
            </div>
          </div>
          <div class="presentation-page__highlight-panel">
            <p class="presentation-page__highlight-panel-eyebrow">Ab Jahr 21 — voller Cashflow</p>
            <p class="presentation-page__highlight-panel-value">{{ cashFlowFromYear21PerMonth }}</p>
            <p class="presentation-page__highlight-panel-text">ohne Darlehensbelastung</p>
          </div>
        </aside>
      </div>
      <p class="presentation-page__disclaimer">
        Musterberechnung ohne Haftung auf Richtigkeit. Keine Steuer- oder Rechtsberatung.
      </p>
    </PresentationPage>
  </PrintPageLandscape>
</template>

<script>
import PrintPageLandscape from '@/components/Print/PrintPageLandscape.vue';
import PresentationPage from '@/views/MasterPresentation/PresentationPage';
import { formatNumber, NUMBER_FORMAT, parseFiniteNumber } from '@/helpers/numberFormat';

function formatTableAmount(value) {
  return formatNumber(value, NUMBER_FORMAT.DECIMAL_ZERO);
}

function loanYearToCalendarYear(loanYear) {
  const currentYear = new Date().getFullYear();
  return currentYear + loanYear - 1;
}

export default {
  name: 'MasterPresentationPrintPage39',
  components: { PrintPageLandscape, PresentationPage },
  props: {
    project: { type: Object, default: null },
    pageCount: { type: Number, default: null },
    pageNumber: { type: Number, default: 0 },
  },
  computed: {
    iab() {
      return this.project?.investmentDeductionAllowance ?? {};
    },

    parameterKeyResults() {
      return this.project?.finModel?.parameterKeyResults ?? {};
    },

    seniorLoan() {
      return formatNumber(
        parseFiniteNumber(this.parameterKeyResults.seniorLoan),
        NUMBER_FORMAT.CURRENCY
      );
    },

    durationYears() {
      return `${formatNumber(this.iab.durationYears, NUMBER_FORMAT.DECIMAL_ZERO)} Jahre`;
    },

    interestRatePercent() {
      return `${formatNumber(this.iab.interestRatePercent, NUMBER_FORMAT.DECIMAL)} %`;
    },

    repaymentRatePercent() {
      return `${formatNumber(this.iab.repaymentRatePercent, NUMBER_FORMAT.DECIMAL)} %`;
    },

    fixedRateYears() {
      return `${formatNumber(this.iab.fixedRateYears, NUMBER_FORMAT.DECIMAL_ZERO)} Jahre`;
    },

    annuity() {
      return formatNumber(
        parseFiniteNumber(this.project?.finModel?.annuity),
        NUMBER_FORMAT.CURRENCY
      );
    },

    amortizationPlan() {
      return this.project?.finModel?.amortizationPlan ?? [];
    },

    loanRows() {
      return this.amortizationPlan.map((row) => ({
        loanYear: row.year,
        year: loanYearToCalendarYear(row.year),
        revenue: formatTableAmount(row.revenue),
        interest: formatTableAmount(row.interest),
        repayment: formatTableAmount(row.repayment),
        cashflowYear: formatTableAmount(row.cashFlowBeforeTax),
        cashflowMonth: formatTableAmount(row.cashFlowBeforeTaxPerMonth),
      }));
    },

    cashFlowFromYear21PerMonth() {
      const formattedAmount = formatNumber(
        this.project?.finModel?.cashFlowFromYear21PerMonth,
        NUMBER_FORMAT.CURRENCY_ROUNDED
      );
      return `≈ ${formattedAmount}/Mo.`;
    },

    loanMeta() {
      return [
        { label: 'Darlehensbetrag', value: this.seniorLoan },
        { label: 'Zinssatz', value: this.interestRatePercent },
        { label: 'Tilgungssatz', value: this.repaymentRatePercent },
        { label: 'Festschreibung', value: this.fixedRateYears },
        { label: 'Laufzeit gesamt', value: this.durationYears },
        { label: 'Annuität (J. 1)', value: this.annuity },
      ];
    },
  },
};
</script>
